export const INSTANCE_EXPRESSION_SCOPE = Object.freeze({
    params: Object.freeze(['instance', 'context']),
    implicitScope: 'instance',
});
export function hasOwn(object, key) {
    return (object != null &&
        (typeof object === 'object' || typeof object === 'function') &&
        Object.prototype.hasOwnProperty.call(object, key));
}
export function readOwn(object, key) {
    return hasOwn(object, key)
        ? object[key]
        : undefined;
}
export function isUnsafeKey(key) {
    return key === '__proto__';
}
function stringify(fragment) {
    if (typeof fragment === 'function') {
        return '<function>';
    }
    try {
        return JSON.stringify(fragment) ?? String(fragment);
    }
    catch {
        return String(fragment);
    }
}
export class RuleCompileError extends Error {
    constructor(definition, path, message) {
        super(message);
        this.definition = definition;
        this.path = path;
        this.name = 'RuleCompileError';
    }
}
export function invalidAt(site, message, ...fragment) {
    const where = site.path ? `${site.path}: ` : '';
    const what = fragment.length ? `: ${stringify(fragment[0])}` : '';
    throw new RuleCompileError(site.definition, site.path, `Invalid ${site.definition}: ${where}${message}${what}`);
}
export function didYouMean(name, candidates) {
    const lower = name.toLowerCase();
    for (const candidate of candidates) {
        if (candidate && candidate !== name && candidate.toLowerCase() === lower) {
            return `; did you mean '${candidate}'?`;
        }
    }
    return '';
}
export function at(site, key, spec) {
    const path = typeof key === 'number'
        ? `${site.path}[${key}]`
        : site.path
            ? `${site.path}.${key}`
            : key;
    return {
        ...site,
        path,
        extraKeys: undefined,
        expression: spec?.expression ?? site.expression,
        call: spec?.call ?? site.call,
    };
}
const LEAF_KINDS = new Set([
    'string',
    'number',
    'boolean',
    'true',
    'scalar',
    'literal',
    'priority',
    'expression',
]);
export function isLeafKind(kind) {
    return LEAF_KINDS.has(kind);
}
const LEAF_CHECKS = {
    string: ['a string', (v) => typeof v === 'string'],
    expression: ['an expression string', (v) => typeof v === 'string'],
    number: [
        'a finite number',
        (v) => typeof v === 'number' && Number.isFinite(v),
    ],
    boolean: ['a boolean', (v) => typeof v === 'boolean'],
    true: ['true', (v) => v === true],
    scalar: [
        'a string, a finite number or a boolean',
        (v) => typeof v === 'string' ||
            typeof v === 'boolean' ||
            (typeof v === 'number' && Number.isFinite(v)),
    ],
    literal: ['any value', () => true],
    priority: [
        'a finite number, or null',
        (v) => v === null || (typeof v === 'number' && Number.isFinite(v)),
    ],
};
function checkLeaf(spec, value, site, label = 'value') {
    const [expected, test] = LEAF_CHECKS[spec.kind];
    if (!test(value)) {
        invalidAt(site, `expected ${expected}`, value);
    }
    if (spec.oneOf && !spec.oneOf.includes(value)) {
        invalidAt(site, `unknown ${label} ${stringify(value)}; allowed: ${spec.oneOf.join(', ')}`);
    }
}
export function describeForm(form, scope) {
    const names = [];
    const operators = [];
    for (const [key, spec] of Object.entries(form.keys)) {
        if (spec.role === 'operator') {
            operators.push(key);
            continue;
        }
        if (key === '*') {
            names.push('<any key>');
            continue;
        }
        names.push(spec.optional || spec.role === 'modifier' ? `${key}?` : key);
    }
    if (operators.length) {
        names.splice(1, 0, `<one of ${operators.join(' | ')}>`);
    }
    const text = `{ ${names.join(', ')} }`;
    const expressionScope = form.expression ?? ('expression' in form.keys ? scope : undefined);
    return expressionScope
        ? `${text} called with (${expressionScope.params.join(', ')})`
        : text;
}
export function describeShape(shape, site) {
    const scope = site?.expression;
    const parts = [];
    if (shape.string === 'expression') {
        parts.push(scope
            ? `an expression string called with (${scope.params.join(', ')})`
            : 'an expression string');
    }
    else if (shape.string === 'attribute') {
        parts.push('a name (a DICOM keyword or a context name)');
    }
    for (const form of Object.values(shape.forms)) {
        parts.push(describeForm(form, scope));
    }
    if (shape.function) {
        parts.push(site?.call ? `a function ${site.call}` : 'a function');
    }
    if (parts.length === 1) {
        return parts[0];
    }
    return `${parts.slice(0, -1).join(', ')}, or ${parts[parts.length - 1]}`;
}
export function unrecognized(shape, fragment, site) {
    return invalidAt(site, `unrecognized ${shape.name}; expected ${describeShape(shape, site)}`, fragment);
}
export function selectForm(shape, fragment, site) {
    if (fragment && typeof fragment === 'object' && !Array.isArray(fragment)) {
        let fallback;
        for (const entry of Object.entries(shape.forms)) {
            const { selectBy } = entry[1];
            if (selectBy === undefined) {
                fallback ??= entry;
            }
            else if (Object.prototype.hasOwnProperty.call(fragment, selectBy)) {
                return entry;
            }
        }
        if (fallback) {
            return fallback;
        }
    }
    return unrecognized(shape, fragment, site);
}
export function checkForm(form, fragment, site, keyNoun = 'key') {
    const allowed = {
        ...form.keys,
        ...site.extraKeys,
    };
    const wildcard = allowed['*'];
    for (const key of Object.keys(fragment)) {
        if (!Object.prototype.hasOwnProperty.call(allowed, key) && !wildcard) {
            invalidAt(site, `unknown ${keyNoun} '${key}'${didYouMean(key, Object.keys(allowed))}; allowed: ${Object.keys(allowed).join(', ')}`);
        }
    }
    const operators = [];
    let hasOperatorKeys = false;
    for (const [key, spec] of Object.entries(allowed)) {
        if (key === '*') {
            continue;
        }
        if (spec.role === 'operator') {
            hasOperatorKeys = true;
        }
        const present = Object.prototype.hasOwnProperty.call(fragment, key) &&
            fragment[key] !== undefined;
        if (!present) {
            if (!spec.optional && !spec.role) {
                invalidAt(site, `missing ${keyNoun} '${key}'`, fragment);
            }
            continue;
        }
        if (spec.role === 'operator') {
            operators.push(key);
        }
        if (isLeafKind(spec.kind)) {
            const keySite = at(site, key, spec);
            if (spec.list) {
                const list = fragment[key];
                if (!Array.isArray(list)) {
                    invalidAt(keySite, 'expected an array', list);
                }
                list.forEach((entry, index) => checkLeaf(spec, entry, at(keySite, index), key));
            }
            else {
                checkLeaf(spec, fragment[key], keySite, key);
            }
        }
    }
    if (hasOperatorKeys) {
        const all = Object.entries(allowed)
            .filter(([, spec]) => spec.role === 'operator')
            .map(([key]) => key);
        const subject = form.selectBy
            ? `${form.selectBy} ${stringify(fragment[form.selectBy])}`
            : 'this form';
        if (!operators.length) {
            invalidAt(site, `no operator for ${subject}; expected exactly one of: ${all.join(', ')}`, fragment);
        }
        if (operators.length > 1) {
            invalidAt(site, `more than one operator for ${subject} (${operators.join(', ')}); expected exactly one of: ${all.join(', ')}`, fragment);
        }
    }
    for (const [key, spec] of Object.entries(allowed)) {
        if (spec.role === 'modifier' &&
            fragment[key] !== undefined &&
            spec.with &&
            !spec.with.some((operator) => operators.includes(operator))) {
            invalidAt(site, `'${key}' applies only with ${spec.with.join(', ')}`, fragment);
        }
    }
}
export function compileKey(spec, value, site) {
    if (spec.function && typeof value === 'function') {
        return value;
    }
    if (spec.list) {
        if (!Array.isArray(value)) {
            if (isLeafKind(spec.kind)) {
                return invalidAt(site, 'expected an array', value);
            }
            const shape = site.schema[spec.kind];
            const entries = shape ? describeShape(shape, site) : spec.kind;
            return invalidAt(site, spec.function
                ? `expected an array of (${entries}), or a function${site.call ? ` ${site.call}` : ''}`
                : `expected an array of (${entries})`, value);
        }
        const entrySpec = { ...spec, list: false, function: false };
        return value.map((entry, index) => compileKey(entrySpec, entry, at(site, index)));
    }
    if (isLeafKind(spec.kind)) {
        checkLeaf(spec, value, site);
        return value;
    }
    if (spec.kind === 'record') {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return invalidAt(site, 'expected an object', value);
        }
        const keys = spec.keys ?? {};
        checkForm({ keys }, value, site);
        const result = {};
        for (const [key, entry] of Object.entries(value)) {
            if (isUnsafeKey(key)) {
                return invalidAt(at(site, key), `the key "${key}" is not allowed`, value);
            }
            const entrySpec = Object.prototype.hasOwnProperty.call(keys, key)
                ? keys[key]
                : keys['*'];
            result[key] = compileKey(entrySpec, entry, at(site, key, entrySpec));
        }
        return result;
    }
    const shape = site.schema[spec.kind];
    const compiler = site.compilers[spec.kind];
    if (!shape || !compiler) {
        return invalidAt(site, `the schema has no compiler for '${spec.kind}'`);
    }
    if (shape.function && typeof value === 'function') {
        return value;
    }
    return compiler(value, { ...site, extraKeys: spec.extraKeys });
}
export function matchForm(shape, fragment, site) {
    const [name, form] = selectForm(shape, fragment, site);
    checkForm(form, fragment, site, shape.keyNoun);
    return [name, form];
}
export const MATCH_OPERATOR_NAMES = Object.freeze([
    'equals',
    'doesNotEqual',
    'notNull',
    'includes',
    'doesNotInclude',
    'greaterThan',
    'lessThan',
    'range',
    'contains',
    'containsI',
    'doesNotContain',
    'doesNotContainI',
    'containsAll',
    'startsWith',
    'endsWith',
]);
export const matchTestShape = {
    name: 'test',
    function: true,
    keyNoun: 'operator',
    forms: {
        operator: {
            keys: Object.fromEntries(MATCH_OPERATOR_NAMES.map((name) => [
                name,
                { kind: 'literal', role: 'operator' },
            ])),
            description: 'One operator and its operand.',
        },
    },
};
export const conditionShape = {
    name: 'condition',
    function: true,
    forms: {
        map: {
            keys: { '*': { kind: 'matchTest' } },
            description: 'A map of name to test. Every entry must hold. `{}` is true.',
        },
    },
};
export const valueShape = {
    name: 'value',
    string: 'attribute',
    function: true,
    forms: {
        template: {
            selectBy: 'template',
            keys: { template: { kind: 'string' } },
            description: 'A string with `{name}` placeholders.',
        },
        expression: {
            selectBy: 'expression',
            keys: { expression: { kind: 'expression' } },
            description: 'An expression; the result is the value, not coerced.',
        },
    },
};
export const safeFunctionSchema = {
    condition: conditionShape,
    matchTest: matchTestShape,
    value: valueShape,
};
